import React, { useState, useEffect } from 'react';
import Card from "components/card";
import { 
  MdSearch, 
  MdError, 
  MdFilterList, 
  MdCalendarToday, 
  MdDelete,
  MdArrowBack,
  MdWarning,
  MdClose,
  MdInventory
} from "react-icons/md";
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { tokenManager } from '../../../utils/tokenManager';

const Loader = () => (
  <div className="flex h-[400px] items-center justify-center">
    <div className="relative">
      <div className="h-24 w-24 rounded-full border-t-4 border-b-4 border-brand-500"></div>
      <div className="absolute top-0 left-0 h-24 w-24 animate-spin rounded-full border-t-4 border-b-4 border-gray-200"></div>
      <div className="absolute top-1/2 left-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-500"></div>
      <div className="mt-8 text-center">
        <p className="text-lg font-medium text-gray-600 dark:text-white">Loading</p>
        <p className="text-sm text-gray-400">Please wait...</p>
      </div>
    </div>
  </div>
);

const DataRecords = () => {
  const navigate = useNavigate();
  
  // State for API data
  const [dataRecords, setDataRecords] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: ''
  });
  const [userRole, setUserRole] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const itemsPerPage = 10;


  // Fetch data from API
  const fetchDataRecords = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const authHeader = tokenManager.getAuthHeader();
      if (!authHeader) {
        throw new Error('Authentication token not found. Please login again.');
      }

      const response = await fetch('https://usibras-web-backend.onrender.com/api/manual-peeling/by-date', {
        method: 'GET',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.data) {
        setDataRecords(result.data);
      } else {
        setDataRecords([]);
      }
      
    } catch (error) {
      console.error('Error fetching data records:', error);
      setError('Failed to load data records. Please try again later.');
      setDataRecords([]);
    } finally {
      setLoading(false);
    }
  };

  // Check user role and fetch data on component mount
  useEffect(() => {
    try {
      const token = localStorage.getItem('usibras_login');
      if (token) {
        const decodedToken = jwtDecode(token);
        setUserRole(decodedToken.role);
      }
    } catch (error) {
      console.error('Error checking user role:', error);
      setUserRole(null);
    }

    fetchDataRecords();
  }, []);

  // Apply filters and search
  const filteredData = dataRecords.filter(item => {
    const searchTermLower = searchTerm.toLowerCase();
    const searchableValues = [
      item.transaction_date,
      item.record_count?.toString(),
      item.total_net_weight?.toString(),
      item.avg_net_weight?.toString()
    ].filter(Boolean);

    const matchesSearch = searchTermLower === '' || 
      searchableValues.some(value => 
        value.toLowerCase().includes(searchTermLower)
      );

    // Convert date strings to Date objects for comparison
    const itemDate = new Date(item.transaction_date);
    const startDate = filters.startDate ? new Date(filters.startDate) : null;
    const endDate = filters.endDate ? new Date(filters.endDate) : null;

    const matchesDate = (!startDate || itemDate >= startDate) &&
                       (!endDate || itemDate <= endDate);

    return matchesSearch && matchesDate;
  });

  // Calculate summary statistics
  const totalRecords = filteredData.length;
  const totalTransactions = filteredData.reduce((sum, item) => sum + item.record_count, 0);
  const totalWeight = filteredData.reduce((sum, item) => sum + item.total_net_weight, 0);

  // Pagination
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const TABLE_HEAD = ["ID", "Transaction Date", "Number of Records", "Total Weight (kg)", "Actions"];

  // Format date for display
  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  // Handle delete confirmation
  const handleDeleteClick = (record) => {
    setRecordToDelete(record);
    setShowDeleteModal(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!recordToDelete) return;

    try {
      setDeleting(true);
      
      const authHeader = tokenManager.getAuthHeader();
      if (!authHeader) {
        throw new Error('Authentication token not found. Please login again.');
      }

      const response = await fetch(`https://usibras-web-backend.onrender.com/api/manual-peeling/by-date?date=${recordToDelete.transaction_date}`, {
        method: 'DELETE',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      // Remove the deleted record from the state
      setDataRecords(prev => prev.filter(record => record.transaction_date !== recordToDelete.transaction_date));
      
      // Close the delete modal
      setShowDeleteModal(false);
      setRecordToDelete(null);
      
      // Show success message
      console.log('Delete successful:', result);
      
    } catch (error) {
      console.error('Error deleting record:', error);
      setError(error.message || 'Failed to delete record. Please try again later.');
    } finally {
      setDeleting(false);
    }
  };

  // Close delete modal
  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setRecordToDelete(null);
  };

  return (
    <div className="mt-5 h-full">
      <Card extra={"w-full h-full p-4"}>
        {/* Header with back button */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <button
              onClick={() => navigate(-1)}
              className="flex w-fit items-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600"
            >
              <MdArrowBack className="h-5 w-5" />
              Back
            </button>
            <div>
              <h4 className="text-lg font-bold text-navy-700 dark:text-white sm:text-xl">
                Manual Peeling Data Records
              </h4>
              <p className="mt-1 text-xs text-gray-600 sm:text-sm">
                View and manage manual peeling data by transaction date
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={fetchDataRecords}
              className="flex w-fit items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
              disabled={loading}
            >
              <MdSearch className="h-4 w-4" />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-3 sm:gap-6">
          <Card extra="p-3 sm:p-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="rounded-full bg-blue-50 p-2 dark:bg-navy-700 sm:p-3">
                <MdCalendarToday className="h-5 w-5 text-blue-600 dark:text-blue-400 sm:h-6 sm:w-6" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Total Dates</p>
                <p className="text-lg font-bold text-navy-700 dark:text-white sm:text-xl">{totalRecords}</p>
              </div>
            </div>
          </Card>

          <Card extra="p-3 sm:p-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="rounded-full bg-green-50 p-2 dark:bg-navy-700 sm:p-3">
                <MdInventory className="h-5 w-5 text-green-600 dark:text-green-400 sm:h-6 sm:w-6" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Total Records</p>
                <p className="text-lg font-bold text-navy-700 dark:text-white sm:text-xl">{totalTransactions}</p>
              </div>
            </div>
          </Card>

          <Card extra="p-3 sm:p-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="rounded-full bg-purple-50 p-2 dark:bg-navy-700 sm:p-3">
                <MdInventory className="h-5 w-5 text-purple-600 dark:text-purple-400 sm:h-6 sm:w-6" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Total Weight</p>
                <p className="text-lg font-bold text-navy-700 dark:text-white sm:text-xl">{totalWeight.toFixed(1)} kg</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filters Section */}
        <div className="mb-8 rounded-xl border border-gray-200 bg-white p-3 dark:border-navy-700 dark:bg-navy-800 sm:p-4">
          <div className="mb-4 flex items-center gap-2">
            <MdFilterList className="h-4 w-4 text-gray-500 sm:h-5 sm:w-5" />
            <h3 className="text-base font-semibold text-gray-700 dark:text-white sm:text-lg">Filters</h3>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 sm:gap-4">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:mb-2 sm:text-sm">Start Date</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({...filters, startDate: e.target.value})}
                className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs dark:border-navy-600 dark:bg-navy-700 sm:px-3 sm:py-2 sm:text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:mb-2 sm:text-sm">End Date</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({...filters, endDate: e.target.value})}
                className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs dark:border-navy-600 dark:bg-navy-700 sm:px-3 sm:py-2 sm:text-sm"
              />
            </div>
          </div>
          <div className="mt-3 flex justify-end sm:mt-4">
            <button
              onClick={() => setFilters({startDate: '', endDate: ''})}
              className="rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600 sm:px-4 sm:py-2 sm:text-sm"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Table header and search */}
        <div className="mb-6 flex flex-col gap-4 sm:mb-8 sm:flex-row sm:items-center sm:justify-between">
          <div className="w-full sm:w-auto">
            <div className="flex h-8 items-center rounded-full bg-white px-3 dark:bg-navy-900 sm:h-10 sm:px-4">
              <span className="text-xl">
                <MdSearch className="h-4 w-4 text-gray-400 sm:h-5 sm:w-5" />
              </span>
               <input
                 type="text"
                 placeholder="Search by date, record count, net weight..."
                 className="ml-2 w-full bg-transparent text-xs font-medium text-gray-600 outline-none dark:text-white sm:text-sm"
                 value={searchTerm}
                 onChange={(e) => setSearchTerm(e.target.value)}
               />
            </div>
          </div>
        </div>

        {loading ? (
          <Loader />
        ) : error ? (
          <div className="flex h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mb-4 text-5xl text-red-500">
                <MdError className="mx-auto h-16 w-16" />
              </div>
              <p className="text-xl font-semibold text-gray-700 dark:text-white">
                Oops! Something went wrong
              </p>
              <p className="mt-2 text-gray-500 dark:text-gray-400">{error}</p>
              <button
                onClick={fetchDataRecords}
                className="mt-4 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
              >
                Try Again
              </button>
            </div>
          </div>
        ) : dataRecords.length === 0 ? (
          <div className="flex h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mb-4 text-5xl text-gray-400">
                <MdInventory className="mx-auto h-16 w-16" />
              </div>
              <p className="text-xl font-semibold text-gray-700 dark:text-white">
                No data records found
              </p>
              <p className="mt-2 text-gray-500 dark:text-gray-400">
                No manual peeling data records are available at the moment.
              </p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr>
                  {TABLE_HEAD.map((head, index) => (
                    <th
                      key={index}
                      className="border-b border-gray-200 pb-2 pr-2 text-start dark:!border-navy-700 sm:pb-[10px] sm:pr-4"
                    >
                      <p className="text-xs font-bold tracking-wide text-gray-800 dark:text-white sm:text-sm">
                        {head}
                      </p>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((item) => (
                  <tr key={item.id}>
                    <td className="pt-2 pb-3 pr-2 sm:pt-[14px] sm:pb-[18px] sm:pr-4">
                      <p className="text-xs font-bold text-navy-700 dark:text-white sm:text-sm">
                        {item.id}
                      </p>
                    </td>
                    <td className="pt-2 pb-3 pr-2 sm:pt-[14px] sm:pb-[18px] sm:pr-4">
                      <p className="text-xs font-bold text-navy-700 dark:text-white sm:text-sm">
                        {formatDate(item.transaction_date)}
                      </p>
                    </td>
                     <td className="pt-2 pb-3 pr-2 sm:pt-[14px] sm:pb-[18px] sm:pr-4">
                       <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                         {item.record_count}
                       </span>
                     </td>
                     <td className="pt-2 pb-3 pr-2 sm:pt-[14px] sm:pb-[18px] sm:pr-4">
                       <p className="text-xs font-bold text-navy-700 dark:text-white sm:text-sm">
                         {item.total_net_weight.toFixed(1)} kg
                       </p>
                     </td>
                    <td className="pt-2 pb-3 pr-2 sm:pt-[14px] sm:pb-[18px] sm:pr-4">
                      <div className="flex items-center gap-1 sm:gap-2">
                        <button
                          onClick={() => handleDeleteClick(item)}
                          className="rounded-lg bg-red-100 p-1.5 text-red-600 hover:bg-red-200 dark:bg-navy-700 dark:text-red-400 dark:hover:bg-navy-600 sm:p-2"
                          title="Delete Records"
                        >
                          <MdDelete className="h-3 w-3 sm:h-4 sm:w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {filteredData.length > 0 && (
          <div className="mt-4 flex flex-col gap-4 sm:mt-6 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs font-medium text-navy-700 dark:text-white sm:text-sm">
              Showing {paginatedData.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} to {Math.min(currentPage * itemsPerPage, filteredData.length)} of {filteredData.length} entries
            </p>
            <div className="flex flex-wrap gap-1 sm:gap-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className={`linear rounded-md px-2 py-1 text-xs font-medium transition duration-200 sm:px-3 sm:text-sm ${
                  currentPage === 1
                    ? "cursor-not-allowed bg-gray-300 text-gray-500"
                    : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
                }`}
              >
                <span className="hidden sm:inline">First</span>
                <span className="sm:hidden">1</span>
              </button>
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className={`linear rounded-md px-2 py-1 text-xs font-medium transition duration-200 sm:px-3 sm:text-sm ${
                  currentPage === 1
                    ? "cursor-not-allowed bg-gray-300 text-gray-500"
                    : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
                }`}
              >
                <span className="hidden sm:inline">Previous</span>
                <span className="sm:hidden">‹</span>
              </button>
              
              {/* Page number buttons */}
              {[...Array(Math.min(3, totalPages))].map((_, index) => {
                let pageNum;
                if (totalPages <= 3) {
                  pageNum = index + 1;
                } else if (currentPage <= 2) {
                  pageNum = index + 1;
                } else if (currentPage >= totalPages - 1) {
                  pageNum = totalPages - 2 + index;
                } else {
                  pageNum = currentPage - 1 + index;
                }
                
                return (
                  <button
                    key={index}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`linear rounded-md px-2 py-1 text-xs font-medium transition duration-200 sm:px-3 sm:text-sm ${
                      currentPage === pageNum
                        ? "bg-brand-700 text-white"
                        : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages || totalPages === 0}
                className={`linear rounded-md px-2 py-1 text-xs font-medium transition duration-200 sm:px-3 sm:text-sm ${
                  currentPage === totalPages || totalPages === 0
                    ? "cursor-not-allowed bg-gray-300 text-gray-500"
                    : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
                }`}
              >
                <span className="hidden sm:inline">Next</span>
                <span className="sm:hidden">›</span>
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages || totalPages === 0}
                className={`linear rounded-md px-2 py-1 text-xs font-medium transition duration-200 sm:px-3 sm:text-sm ${
                  currentPage === totalPages || totalPages === 0
                    ? "cursor-not-allowed bg-gray-300 text-gray-500"
                    : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
                }`}
              >
                <span className="hidden sm:inline">Last</span>
                <span className="sm:hidden">{totalPages}</span>
              </button>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && recordToDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
            <div className="relative w-full max-w-md rounded-xl bg-white p-4 dark:bg-navy-800 sm:p-6">
              {/* Modal Header */}
              <div className="mb-4 flex items-center justify-between border-b border-gray-200 pb-3 dark:border-navy-600 sm:mb-6 sm:pb-4">
                <div>
                  <h3 className="text-lg font-bold text-navy-700 dark:text-white sm:text-xl">
                    Delete Data Records
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400 sm:text-sm">
                    This action cannot be undone
                  </p>
                </div>
                <button
                  onClick={closeDeleteModal}
                  className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-navy-700 dark:hover:text-white sm:p-2"
                >
                  <MdClose className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="mb-6">
                <div className="mb-4 flex items-center gap-3">
                  <div className="rounded-full bg-red-100 p-2 dark:bg-red-900/20">
                    <MdDelete className="h-6 w-6 text-red-600 dark:text-red-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-white">
                      Are you sure you want to delete all records for this date?
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Date: {formatDate(recordToDelete.transaction_date)}
                    </p>
                  </div>
                </div>
                 <div className="rounded-lg bg-red-50 p-3 dark:bg-red-900/10">
                   <p className="text-xs text-red-700 dark:text-red-300">
                     This will permanently delete {recordToDelete.record_count} records ({recordToDelete.total_net_weight.toFixed(1)} kg) for {formatDate(recordToDelete.transaction_date)}. 
                     This action cannot be undone.
                   </p>
                 </div>
              </div>

              {/* Modal Footer */}
              <div className="flex gap-3">
                <button
                  onClick={closeDeleteModal}
                  disabled={deleting}
                  className="flex-1 rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={deleting}
                  className="flex-1 rounded-lg bg-red-500 px-3 py-2 text-sm font-medium text-white hover:bg-red-600 disabled:opacity-50"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
       </Card>
     </div>
   );
 };

export default DataRecords;
